import axiosInstance from './axiosInstance';

interface TaskFilters {
  projectId?: string;
  equipmentId?: string;
  assignedEngineerId?: string;
  checkpointId?: string;
  status?: string;
  outcome?: string;
  frequency?: string;
  startDateFrom?: string;
  startDateTo?: string;
  approvalStatus?: 'approved' | 'pending' | 'rejected';
  sortBy?: string;
  limit?: number;
  page?: number;
}

interface CreateTaskData {
  projectId?: string;
  equipmentId?: string;
  checkpointId?: string;
  checkpointType?: 'ProjectAssetCheckpoint' | 'EquipmentCheckpoint';
  startDate?: string;
  endDate?: string;
  frequency?: string;
  specification?: string;
}

interface ApprovalData {
  reason?: string;
}

interface TaskServiceResponse<T = any> {
  success: boolean;
  message: string;
  data: T;
}

export interface EquipmentTaskHistoryItem {
  _id: string;
  checkpoint: string | null;
  frequency: string | null;
  status: string;
  outcome: 'Pass' | 'Fail' | null;
  completedAt: string | null;
  completedBy: { _id: string; name: string | null } | null;
  assignedEngineer: { _id: string; name: string | null } | null;
  remarks: string | null;
  photoCount: number;
  moduleScope: { grouped: boolean; stringCount: number; moduleCount: number } | null;
  stringIndex: number | null;
}

export interface EquipmentTaskHistoryResponse {
  summary: { totalCompleted: number; passed: number; failed: number; lastActivityAt: string | null };
  results: EquipmentTaskHistoryItem[];
  page: number;
  limit: number;
  totalPages: number;
}

class TaskService {
  private baseUrl = '/tasks';

  // Basic CRUD operations
  async getTasks(filters: TaskFilters = {}): Promise<TaskServiceResponse> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });

    const response = await axiosInstance.get(`${this.baseUrl}?${params.toString()}`);
    return response.data;
  }

  async getTaskById(taskId: string): Promise<TaskServiceResponse> {
    const response = await axiosInstance.get(`${this.baseUrl}/${taskId}`);
    return response.data;
  }

  async getEquipmentTaskHistory(
    equipmentId: string,
    page = 1,
    limit = 20
  ): Promise<TaskServiceResponse<EquipmentTaskHistoryResponse>> {
    const response = await axiosInstance.get(
      `${this.baseUrl}/equipment/${equipmentId}/history?page=${page}&limit=${limit}`
    );
    return response.data;
  }

  /** Calendar: month-wise data (frequency counts, summary pass/fail/issues, tasks with assignee) */
  async getCalendarMonth(projectId: string, startDate: string, endDate: string): Promise<TaskServiceResponse> {
    const params = new URLSearchParams({ projectId, startDate, endDate });
    const response = await axiosInstance.get(`${this.baseUrl}/calendar?${params.toString()}`);
    return response.data;
  }

  async createTask(taskData: CreateTaskData): Promise<TaskServiceResponse> {
    const response = await axiosInstance.post(this.baseUrl, taskData);
    return response.data;
  }

  async updateTask(taskId: string, updateData: Partial<CreateTaskData>): Promise<TaskServiceResponse> {
    const response = await axiosInstance.patch(`${this.baseUrl}/${taskId}`, updateData);
    return response.data;
  }

  async deleteTask(taskId: string): Promise<TaskServiceResponse> {
    const response = await axiosInstance.delete(`${this.baseUrl}/${taskId}`);
    return response.data;
  }

  // Task execution operations
  async startTask(taskId: string, beforeImages?: string[]): Promise<TaskServiceResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/${taskId}/start`, {
      beforeImages: beforeImages || []
    });
    return response.data;
  }

  async putTaskOnHold(taskId: string, reason?: string, remarks?: string): Promise<TaskServiceResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/${taskId}/hold`, {
      reason,
      remarks
    });
    return response.data;
  }

  async completeTask(
    taskId: string, 
    outcome: 'Pass' | 'Fail', 
    afterImages?: string[], 
    remarks?: string,
    issue?: { description: string; priority: 'Low' | 'Medium' | 'High' | 'Critical' }
  ): Promise<TaskServiceResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/${taskId}/complete`, {
      outcome,
      afterImages: afterImages || [],
      remarks,
      issue
    });
    return response.data;
  }

  async reassignTask(
    taskId: string, 
    newEngineerId: string, 
    reason: string
  ): Promise<TaskServiceResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/${taskId}/reassign`, {
      newEngineerId,
      reason
    });
    return response.data;
  }

  async resolveIssue(
    taskId: string, 
    actionTaken: string, 
    resolutionImages?: string[]
  ): Promise<TaskServiceResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/${taskId}/resolve`, {
      actionTaken,
      resolutionImages: resolutionImages || []
    });
    return response.data;
  }

  // Bulk operations
  async bulkGenerateTasks(data: {
    'project-id': string;
    'equipment-ids'?: string[];
    'checkpoint-ids'?: string[];
    'start-date': string;
    'end-date': string;
  }): Promise<TaskServiceResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/bulk-generate`, data);
    return response.data;
  }

  async assignTasksToEngineer(data: {
    'site-engineer-id': string;
    'task-ids'?: string[];
    'equipment-ids'?: string[];
    reason?: string;
  }): Promise<TaskServiceResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/assign-to-engineer`, data);
    return response.data;
  }

  // Approval operations
  async approveTask(taskId: string, approvalData: ApprovalData = {}): Promise<TaskServiceResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/${taskId}/approve`, approvalData);
    return response.data;
  }

  async rejectTask(taskId: string, rejectionData: { reason: string }): Promise<TaskServiceResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/${taskId}/reject`, rejectionData);
    return response.data;
  }

  async getPendingApprovals(filters: TaskFilters = {}): Promise<TaskServiceResponse> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });

    const response = await axiosInstance.get(`${this.baseUrl}/pending-approvals?${params.toString()}`);
    return response.data;
  }

  // Maintenance operations
  async updateOverdueTasks(): Promise<TaskServiceResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/update-overdue`);
    return response.data;
  }

  async getTaskStatistics(taskId: string): Promise<TaskServiceResponse> {
    const response = await axiosInstance.get(`${this.baseUrl}/${taskId}/statistics`);
    return response.data;
  }

  // Helper methods for filtering
  getTasksByStatus(status: string, additionalFilters: TaskFilters = {}): Promise<TaskServiceResponse> {
    return this.getTasks({ ...additionalFilters, status });
  }

  getTasksByApprovalStatus(approvalStatus: 'approved' | 'pending' | 'rejected', additionalFilters: TaskFilters = {}): Promise<TaskServiceResponse> {
    return this.getTasks({ ...additionalFilters, approvalStatus });
  }

  getTasksByProject(projectId: string, additionalFilters: TaskFilters = {}): Promise<TaskServiceResponse> {
    return this.getTasks({ ...additionalFilters, projectId });
  }

  getTasksByEngineer(assignedEngineerId: string, additionalFilters: TaskFilters = {}): Promise<TaskServiceResponse> {
    return this.getTasks({ ...additionalFilters, assignedEngineerId });
  }

  getOverdueTasks(additionalFilters: TaskFilters = {}): Promise<TaskServiceResponse> {
    return this.getTasks({ ...additionalFilters, status: 'overdue' });
  }

  getPendingTasks(additionalFilters: TaskFilters = {}): Promise<TaskServiceResponse> {
    return this.getTasks({ ...additionalFilters, status: 'due' });
  }

  getInProgressTasks(additionalFilters: TaskFilters = {}): Promise<TaskServiceResponse> {
    return this.getTasks({ ...additionalFilters, status: 'inprogress' });
  }

  getCompletedTasks(additionalFilters: TaskFilters = {}): Promise<TaskServiceResponse> {
    return this.getTasks({ 
      ...additionalFilters, 
      status: 'pass,fail', // Get both passed and failed tasks
    });
  }

  // Equipment-focused methods
  async getEquipmentTaskStats(projectId?: string): Promise<TaskServiceResponse> {
    const params = new URLSearchParams();
    if (projectId) {
      params.append('projectId', projectId);
    }
    
    const response = await axiosInstance.get(`${this.baseUrl}/equipment-stats?${params.toString()}`);
    return response.data;
  }

  async getEquipmentCheckpoints(equipmentId: string): Promise<TaskServiceResponse> {
    const response = await axiosInstance.get(`${this.baseUrl}/equipment/${equipmentId}/checkpoints`);
    return response.data;
  }

  async generateTasksByEquipment(data: {
    equipmentIds: string[];
    startDate: string;
    endDate: string;
    projectId: string;
  }): Promise<TaskServiceResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/generate-by-equipment`, data);
    return response.data;
  }

  async getDateWiseTasks(userId: string, startDate?: string, endDate?: string): Promise<TaskServiceResponse> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await axiosInstance.get(`${this.baseUrl}/date-wise/${userId}?${params.toString()}`);
    return response.data;
  }
}

export default new TaskService();